import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, distinctUntilChanged, Observable, of } from 'rxjs';
import { catchError, share, shareReplay, tap } from 'rxjs/operators';
import { MessageService } from '@src/app/_services/message.service';
import { User } from '@src/app/_models/user/user';
import { Hotline } from '@src/app/_models/hotline/hotline';
import { PermissionService } from '@src/app/_services/permission.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Company } from '@src/app/_models/company/company';
import { AuthenticationService } from '@src/app/_services/authentication.service';
import { Department } from '@src/app/_models/department/department';
import { Role } from '@src/app/_models/role/role';
import { Employer } from '@src/app/_models/employer/employer';
import { Hotliner } from '@src/app/_models/hotline/hotliner';
import { Location, registerLocaleData } from '@angular/common';
import { HotlineReport } from '@src/app/_models/hotline/report';
import moment from 'moment';
import localeCs from '@angular/common/locales/cs';
import { EnvironmentService } from '@src/app/_services/environment.service';

moment.updateLocale('cs', { workingWeekdays: [1, 2, 3, 4, 5] });
registerLocaleData(localeCs);

@Injectable({
    providedIn: 'root',
})
export class DataService {
    private readonly usersUrl: string;

    private readonly hotlineUrl: string;

    private readonly companyrUrl: string;

    private readonly departmentUrl: string;

    private readonly roleUrl: string;

    private readonly employerUrl: string;

    private readonly hotlinersUrl: string;

    private readonly resultCompanySource = new BehaviorSubject<Company[] | undefined>([]);

    private readonly resultCompanyDetailSource = new BehaviorSubject<Company | null | undefined>(
        null
    );

    private readonly resultDepartmentSource = new BehaviorSubject<Department[] | undefined>([]);

    private readonly resultDepartmentDetailSource = new BehaviorSubject<
        Department | null | undefined
    >(null);

    private readonly resultEmployerSource = new BehaviorSubject<Employer[] | undefined>([]);

    private readonly resultEmployerDetailSource = new BehaviorSubject<Employer | null | undefined>(
        null
    );

    private readonly resultRoleSource = new BehaviorSubject<Role[] | undefined>([]);

    private readonly resultRoleDetailSource = new BehaviorSubject<Role | null | undefined>(null);

    private readonly resultUserSource = new BehaviorSubject<User[] | undefined>([]);

    private readonly resultUserDetailSource = new BehaviorSubject<User | null | undefined>(null);

    private readonly resultHotlineSource = new BehaviorSubject<Hotline[] | undefined>([]);

    private readonly resultHotlinersSource = new BehaviorSubject<Hotliner[] | undefined>([]);

    private readonly resultHotlineReportSource = new BehaviorSubject<HotlineReport[] | undefined>(
        []
    );

    private readonly resultHotlineDetailSource = new BehaviorSubject<Hotline | null | undefined>(
        null
    );

    private readonly resultHotlinerDetailSource = new BehaviorSubject<Hotliner | null | undefined>(
        null
    );

    companySource = this.resultCompanySource.asObservable().pipe(distinctUntilChanged());

    departmentSource = this.resultDepartmentSource.asObservable().pipe(distinctUntilChanged());

    employerSource = this.resultEmployerSource.asObservable().pipe(distinctUntilChanged());

    roleSource = this.resultRoleSource.asObservable().pipe(distinctUntilChanged());

    userSource = this.resultUserSource.asObservable().pipe(distinctUntilChanged());

    userDetailSource = this.resultUserDetailSource.asObservable().pipe(distinctUntilChanged());

    hotlineSource = this.resultHotlineSource.asObservable().pipe(distinctUntilChanged());

    hotlinersSource = this.resultHotlinersSource.asObservable().pipe(distinctUntilChanged());

    hotlineReportSource = this.resultHotlineReportSource
        .asObservable()
        .pipe(distinctUntilChanged());

    hotlineDetailSource = this.resultHotlineDetailSource
        .asObservable()
        .pipe(distinctUntilChanged());

    hotlinerSource = this.resultHotlinerDetailSource.asObservable().pipe(distinctUntilChanged());

    // eslint-disable-next-line max-lines-per-function
    constructor(
        private readonly http: HttpClient,
        private readonly authenticationService: AuthenticationService,
        private readonly permissionsService: PermissionService,
        private readonly location: Location,
        private readonly sanitizer: DomSanitizer,
        private readonly messageService: MessageService,
        private readonly environmentService: EnvironmentService
    ) {
        this.usersUrl = `${this.environmentService.backendURL}/api/user`;
        this.hotlineUrl = `${this.environmentService.backendURL}/api/hotline`;
        this.companyrUrl = `${this.environmentService.backendURL}/api/company/`;
        this.departmentUrl = `${this.environmentService.backendURL}/api/department`;
        this.roleUrl = `${this.environmentService.backendURL}/api/role`;
        this.employerUrl = `${this.environmentService.backendURL}/api/employer`;
        this.hotlinersUrl = `${this.environmentService.backendURL}/api/hotliners`;
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result? - optional value to return as the observable result
     */
    private handleError<T>(operation = 'operation', result?: T) {
        return (error: { [key: string]: string }): Observable<T | undefined> => {
            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to console instead
            // TODO: better job of transforming error for user consumption
            DataService.log(`${operation} failed: ${error.message}`);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return of(result);
        };
    }

    /** Log a ticketService message with the MessageService */
    private static log(message: string): void {
        console.info(message);
    }

    // COMPANIES GETTERS
    private getCompaniesData(): Observable<Company[] | undefined> {
        return this.http.get<Company[]>(this.companyrUrl).pipe(
            tap(() => {
                DataService.log('fetched companies');
            }),
            shareReplay(),
            catchError(this.handleError<Company[]>('getCompaniesQueue'))
        );
    }

    private getCompanyData(id: number): Observable<Company | undefined> {
        const url = `${this.companyrUrl}/search/${id}`;

        return this.http.get<Company>(url).pipe(
            tap(() => {
                DataService.log('fetched company');
            }),
            shareReplay(),
            catchError(this.handleError<Company>('getDetail'))
        );
    }

    // DEPARTMENTS GETTERS
    private getDepartmentsData(): Observable<Department[] | undefined> {
        return this.http.get<Department[]>(this.departmentUrl).pipe(
            tap(() => {
                DataService.log('fetched departments');
            }),
            shareReplay(),
            catchError(this.handleError<Department[]>('getDepartmentQueue'))
        );
    }

    private getDepartmentData(id: number): Observable<Department | undefined> {
        const url = `${this.departmentUrl}/search/${id}`;

        return this.http.get<Department>(url).pipe(
            tap(() => {
                DataService.log('fetched department');
            }),
            shareReplay(),
            catchError(this.handleError<Department>('getDetail'))
        );
    }

    // ROLES GETTERS
    private getRolesData(): Observable<Role[] | undefined> {
        return this.http.get<Role[]>(this.roleUrl).pipe(
            tap(() => {
                DataService.log('fetched roles');
            }),
            shareReplay(),
            catchError(this.handleError<Role[]>('getRoleQueue'))
        );
    }

    private getRoleData(id: number): Observable<Role | undefined> {
        const url = `${this.roleUrl}/search/${id}`;

        return this.http.get<Role>(url).pipe(
            tap(() => {
                DataService.log('fetched role');
            }),
            shareReplay(),
            catchError(this.handleError<Role>('getDetail'))
        );
    }

    // EMPLOYERS GETTERS
    private getEmployersData(): Observable<Employer[] | undefined> {
        return this.http.get<Employer[]>(this.employerUrl).pipe(
            tap(() => {
                DataService.log('fetched employers');
            }),
            shareReplay(),
            catchError(this.handleError<Employer[]>('getEmployerQueue'))
        );
    }

    private getEmployerData(id: number): Observable<Employer | undefined> {
        const url = `${this.employerUrl}/search/${id}`;

        return this.http.get<Employer>(url).pipe(
            tap(() => {
                DataService.log('fetched company');
            }),
            shareReplay(),
            catchError(this.handleError<Employer>('getDetail'))
        );
    }

    // USERS GETTERS
    private getUsersData(): Observable<User[] | undefined> {
        return this.http.get<User[]>(this.usersUrl).pipe(
            tap(() => {
                DataService.log('fetched users');
            }),
            shareReplay(),
            catchError(this.handleError<User[]>('getUsersQueue', []))
        );
    }

    private getUserData(idUser: number): Observable<User | undefined> {
        const url = `${this.usersUrl}/search/${idUser}`;

        return this.http.get<User>(url).pipe(
            tap(() => {
                DataService.log(`fetched users id=${idUser}`);
            }),
            share(),
            catchError(this.handleError<User>(`getUser id=${idUser}`))
        );
    }

    // HOTLINE GETTERS
    private getHotlineData(hotline: Hotline | number): Observable<Hotline | undefined> {
        const id = typeof hotline === 'number' ? hotline : hotline.id;
        const url = `${this.hotlineUrl}/search/${id}`;

        return this.http.get<Hotline>(url).pipe(
            tap(() => {
                DataService.log(`fetched hotline id=${id}`);
            }),
            shareReplay(),
            catchError(this.handleError<Hotline>(`getHotline id=${id}`))
        );
    }

    getHotlinersData(): Observable<Hotliner[] | undefined> {
        return this.http.get<Hotliner[]>(this.hotlinersUrl).pipe(
            tap(() => {
                DataService.log('fetched hotliners');
            }),
            shareReplay(),
            catchError(this.handleError<Hotliner[]>('getHotlinerQueue', []))
        );
    }

    getHotlinesData(user?: User | number): Observable<Hotline[] | undefined> {
        let userId = 0;

        if (user) {
            userId = typeof user === 'number' ? user : user.id;
        }

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const params = new HttpParams().set('user_id', userId.toString());

        return this.http.get<Hotline[]>(this.hotlineUrl, { headers, params }).pipe(
            tap(() => {
                DataService.log('fetched hotlines');
            }),
            shareReplay(),
            catchError(this.handleError<Hotline[]>('getHotlineQueue', []))
        );
    }

    setCompanyDataSource(): void {
        const resultCompanyData: Company[] = [];

        this.getCompaniesData().subscribe((companies: Company[]) => {
            if (typeof companies !== 'undefined' && companies.length > 0) {
                companies.forEach((company: Company) => {
                    resultCompanyData.push(company);
                });
            }

            this.resultCompanySource.next(resultCompanyData);
        });
    }

    // DEPARTMENTS SETTERS
    setDepartmentDetailSource(department: Department | number): void {
        const id = typeof department === 'number' ? department : department.id;

        this.getDepartmentData(id).subscribe((data: Department) => {
            if (
                this.authenticationService.currentUserValue &&
                this.permissionsService.checkUserISAdministrative(
                    this.authenticationService.currentUserValue
                )
            ) {
                this.resultDepartmentDetailSource.next(data);
            } else {
                this.resultDepartmentDetailSource.next(null);

                const body = 'Nejste oprávněn nahlížet do tohoto oddělení...';
                const options = { progressBar: true, timeOut: 5000, toastClass: 'red' };

                this.messageService.showError('Nedostatečné oprávnění', body, options);
                this.location.back();
            }
        });
    }

    setDepartmentDataSource(): void {
        const resultDepartmentData: Department[] = [];

        this.getDepartmentsData().subscribe((departments: Department[] | undefined) => {
            if (typeof departments !== 'undefined' && departments.length > 0) {
                departments.forEach((department: Department) => {
                    resultDepartmentData.push(department);
                });
            }

            this.resultDepartmentSource.next(resultDepartmentData);
        });
    }

    // ROLES SETTERS
    setRoleDetailSource(role: Role | number): void {
        const id = typeof role === 'number' ? role : role.id;

        this.getRoleData(id).subscribe((data: Role) => {
            if (
                this.authenticationService.currentUserValue &&
                this.permissionsService.checkUserISAdministrative(
                    this.authenticationService.currentUserValue
                )
            ) {
                this.resultRoleDetailSource.next(data);
            } else {
                this.resultRoleDetailSource.next(null);

                const body = 'Nejste oprávněn nahlížet do této role...';
                const options = { progressBar: true, timeOut: 5000, toastClass: 'red' };

                this.messageService.showError('Nedostatečné oprávnění', body, options);
                this.location.back();
            }
        });
    }

    setRoleDataSource(): void {
        const resultRoleData: Role[] = [];

        this.getRolesData().subscribe((roles: Role[]) => {
            if (typeof roles !== 'undefined' && roles.length > 0) {
                roles.forEach((role: Role) => {
                    resultRoleData.push(role);
                });
            }

            this.resultRoleSource.next(resultRoleData);
        });
    }

    // EMPLOYERS SETTERS
    setEmployerDetailSource(employer: Employer | number): void {
        const id = typeof employer === 'number' ? employer : employer.id;

        this.getEmployerData(id).subscribe((data: Employer) => {
            if (
                this.authenticationService.currentUserValue &&
                this.permissionsService.checkUserISAdministrative(
                    this.authenticationService.currentUserValue
                )
            ) {
                this.resultEmployerDetailSource.next(data);
            } else {
                this.resultEmployerDetailSource.next(null);

                const body = 'Nejste oprávněn nahlížet do tohoto zaměstnavatele...';
                const options = { progressBar: true, timeOut: 5000, toastClass: 'red' };

                this.messageService.showError('Nedostatečné oprávnění', body, options);
                this.location.back();
            }
        });
    }

    setEmployerDataSource(): void {
        const resultEmployerData: Employer[] = [];

        this.getEmployersData().subscribe((employers: Employer[]) => {
            if (typeof employers !== 'undefined' && employers.length > 0) {
                employers.forEach((employer: Employer) => {
                    resultEmployerData.push(employer);
                });
            }

            this.resultEmployerSource.next(resultEmployerData);
        });
    }

    // USERS SETTERS
    setUsersDataSource(active?: boolean): void {
        if (this.resultUserSource.getValue()?.length === 0) {
            const resultUserData: User[] = [];

            this.getUsersData().subscribe((users: User[]) => {
                if (typeof users !== 'undefined' && users.length > 0) {
                    // eslint-disable-next-line complexity
                    users.forEach((user: User) => {
                        user.unixCreatedTime = moment(user.created_date).valueOf();
                        user.unixUpdatedTime = user.updated_date
                            ? moment(user.updated_date).valueOf()
                            : null;

                        user.unixDeletedTime = user.deleted_date
                            ? moment(user.deleted_date).valueOf()
                            : null;

                        if (active && user.authorized && !user.deleted_date) {
                            resultUserData.push(user);
                        } else {
                            resultUserData.push(user);
                        }
                    });
                }

                this.resultUserSource.next(resultUserData);
            });
        }
    }

    // eslint-disable-next-line max-lines-per-function
    setUserDetailSource(userData: User | number): void {
        const id = typeof userData === 'number' ? userData : userData.id;

        // eslint-disable-next-line max-lines-per-function
        this.getUserData(id).subscribe((user: User) => {
            user.is_hotliner = false;
            this.getHotlinersData().subscribe((hotliners: Hotliner[]) => {
                user.is_hotliner = this.permissionsService.checkUserISHotliner(hotliners);
            });
            user.unixCreatedTime = moment(user.created_date).valueOf();
            user.unixUpdatedTime = user.updated_date ? moment(user.updated_date).valueOf() : null;
            user.unixDeletedTime = user.deleted_date ? moment(user.deleted_date).valueOf() : null;
            this.resultUserDetailSource.next(user);
        });
    }

    // HOTLINE SETTERS
    setHotlinesDataSource(): void {
        if (this.resultHotlineSource.getValue()?.length === 0) {
            this.getHotlinesData().subscribe((hotlines: Hotline[]) => {
                const resultHotlinesData: Hotline[] = [];

                hotlines.map((hotline: Hotline) => {
                    hotline.unixStartDate = moment(hotline.start_at).valueOf();
                    hotline.unixEndDate = moment(hotline.end_at).valueOf();
                    hotline.unixCreatedTime = moment(hotline.created_date).valueOf();
                    hotline.unixUpdatedTime = moment(hotline.updated_date).valueOf();
                    // eslint-disable-next-line max-nested-callbacks
                    hotline.reports.map((report: HotlineReport, index) => {
                        hotline.reports[index].unixCreatedTime = moment(
                            report.created_date
                        ).valueOf();

                        hotline.reports[index].unixUpdatedTime = moment(
                            report.updated_date
                        ).valueOf();

                        hotline.reports[index].reportHTML = this.sanitizer.bypassSecurityTrustHtml(
                            report.report
                        );
                    });

                    if (this.permissionsService.viewHotline(hotline)) {
                        resultHotlinesData.push(hotline);
                    }
                });
                this.resultHotlineSource.next(resultHotlinesData);
            });
        }
    }

    setHotlineDetailDataSource(hotline: Hotline | number): void {
        const id = typeof hotline === 'number' ? hotline : hotline.id;

        this.getHotlineData(id).subscribe((data: Hotline) => {
            data.unixStartDate = moment(data.start_at).valueOf();
            data.unixEndDate = moment(data.end_at).valueOf();
            data.unixCreatedTime = moment(data.created_date).valueOf();
            data.unixUpdatedTime = moment(data.updated_date).valueOf();
            data.reports.map((report: HotlineReport, index) => {
                data.reports[index].unixCreatedTime = moment(report.created_date).valueOf();
                data.reports[index].unixUpdatedTime = moment(report.updated_date).valueOf();
                data.reports[index].reportHTML = this.sanitizer.bypassSecurityTrustHtml(
                    report.report
                );
            });
            this.resultHotlineDetailSource.next(data);
        });
    }

    // eslint-disable-next-line max-lines-per-function
    setHotlinersDataSource(): void {
        if (this.resultHotlinersSource.getValue()?.length === 0) {
            this.getHotlinersData().subscribe((hotliners: Hotliner[]) => {
                this.getCompaniesData().subscribe((companies: Company[]) => {
                    const resultHotlinersData: Hotliner[] = [];

                    if (typeof hotliners !== 'undefined' && hotliners.length > 0) {
                        // eslint-disable-next-line max-nested-callbacks
                        hotliners.map((hotliner: Hotliner) => {
                            hotliner.score = 0;
                            hotliner.next_hotlines = [];
                            hotliner.assignations = [];
                            hotliner.unixCreatedTime = moment(hotliner.created_date).valueOf();
                            hotliner.unixUpdatedTime = moment(hotliner.updated_date).valueOf();
                            // eslint-disable-next-line max-nested-callbacks
                            hotliner.hotlines.forEach((hotline: Hotline) => {
                                const tempCompany = companies.find(
                                    // eslint-disable-next-line max-nested-callbacks
                                    company => company.id === hotline.company_id
                                );

                                if (typeof tempCompany !== 'undefined') {
                                    hotline.company = tempCompany;
                                }

                                const start = moment(hotline.start_at).format('YYYY-MM-DD');
                                const end = moment(hotline.end_at).format('YYYY-MM-DD');
                                const curr = moment().format('YYYY-MM-DD');

                                if (start >= curr) {
                                    hotliner.next_hotlines.push(hotline);
                                }

                                if (!hotline.deleted_date && end < curr) {
                                    hotliner.score = hotliner.score + hotline.score;
                                }
                            });

                            // eslint-disable-next-line max-nested-callbacks
                            hotliner.next_hotlines = hotliner.next_hotlines.sort((a, b) => {
                                const dateA = moment(a.start_at).valueOf();
                                const dateB = moment(b.start_at).valueOf();

                                return dateA - dateB;
                            });
                            resultHotlinersData.push(hotliner);
                        });
                    }

                    this.resultHotlinersSource.next(resultHotlinersData);
                });
            });
        }
    }

    // CLEAR CACHES
    clearHotlinesCache(): void {
        this.resultHotlineSource.next([]);
    }

    clearHotlinersCache(): void {
        this.resultHotlinersSource.next([]);
    }

    clearUserCache(): void {
        this.resultUserSource.next([]);
    }

    clearAllCache(): void {
        this.resultHotlineSource.next([]);
        this.resultHotlinersSource.next([]);
        this.resultUserSource.next([]);
    }
}
